import { useEffect, useRef, useState, type MouseEvent, type RefObject } from "react";
import type { Row } from "@tanstack/react-table";
import { PAGE_SEARCH_ATTR } from "@/components/SearchInput/pageSearch";
import { hasOpenOverlay, hasTransientPopupOpen } from "@/hooks/useOverlayDismiss";
import { isEditableKeyboardTarget } from "@/lib/keyboardShortcuts";
import { getSolePageTableKeyboard, registerPageTableKeyboard } from "@/lib/pageTableKeyboard";

const PREVIEW_BUTTON_SELECTOR = '[data-testid="expand-details"]';
const TABLE_ROW_SELECTOR = "[data-table-row-id]";
/** Fallback so a later sort does not snap to row 1 if ids never change after search Enter. */
const PERSIST_SELECT_FIRST_MS = 5000;
const INTERACTIVE_SELECTOR = [
  "button",
  "a",
  "summary",
  "input",
  "select",
  "textarea",
  "[role='button']",
  "[role='menuitem']",
  "[role='checkbox']",
  "[role='switch']",
  "[role='combobox']",
  "[role='listbox']",
  "[role='option']",
  "[role='tab']",
  "[role='radio']",
  "[aria-haspopup]"
].join(", ");

function isRowWrapperLink(element: HTMLElement): boolean {
  return element.tagName === "A" && Boolean(element.querySelector(":scope > [data-table-row-id]"));
}

function isMaterialHost(element: Element): boolean {
  return element.localName.startsWith("md-");
}

function isInteractiveElement(element: HTMLElement): boolean {
  if (isRowWrapperLink(element)) return false;
  if (isMaterialHost(element)) return true;

  const tag = element.tagName;
  if (tag === "BUTTON" || tag === "SUMMARY" || tag === "A") return true;

  const popup = element.getAttribute("aria-haspopup");
  if (popup && popup !== "false") return true;

  const closest = element.closest(INTERACTIVE_SELECTOR);
  if (closest instanceof HTMLElement && !isRowWrapperLink(closest)) return true;

  let current: HTMLElement | null = element.parentElement;
  while (current) {
    if (isRowWrapperLink(current)) return false;
    if (isMaterialHost(current)) return true;
    current = current.parentElement;
  }
  return false;
}

function isInteractiveActivationTarget(target: EventTarget | null): boolean {
  return target instanceof HTMLElement && isInteractiveElement(target);
}

function isInteractiveFromKeyEvent(event: KeyboardEvent): boolean {
  for (const node of event.composedPath()) {
    if (node instanceof HTMLElement && isInteractiveElement(node)) return true;
  }
  return isInteractiveActivationTarget(event.target) || isInteractiveActivationTarget(document.activeElement);
}

function isPageSearchElement(element: EventTarget | null): boolean {
  return element instanceof HTMLElement && element.hasAttribute(PAGE_SEARCH_ATTR);
}

function isPageSearchFromKeyEvent(event: KeyboardEvent): boolean {
  for (const node of event.composedPath()) {
    if (isPageSearchElement(node)) return true;
  }
  return isPageSearchElement(event.target) || isPageSearchElement(document.activeElement);
}

function shouldIgnoreTableShortcut(event: KeyboardEvent, options?: { allowRepeat?: boolean }): boolean {
  if (!options?.allowRepeat && event.repeat) return true;
  if (event.metaKey || event.ctrlKey || event.altKey) return true;
  if (isEditableKeyboardTarget(event.target) || isEditableKeyboardTarget(document.activeElement)) return true;
  if (hasOpenOverlay() || hasTransientPopupOpen()) return true;
  if (document.body.dataset.goNavMode === "true") return true;
  return false;
}

function findRowElement(container: HTMLElement | null, rowId: string): HTMLElement | null {
  if (!container) return null;
  return container.querySelector(`${TABLE_ROW_SELECTOR}[data-table-row-id="${CSS.escape(rowId)}"]`);
}

function clickRowLink(rowElement: HTMLElement | null): boolean {
  if (!rowElement) return false;
  const parent = rowElement.parentElement;
  if (!(parent instanceof HTMLAnchorElement) || !parent.getAttribute("href")) return false;
  parent.click();
  return true;
}

function clickPreviewButton(rowElement: HTMLElement | null): boolean {
  const button = rowElement?.querySelector(PREVIEW_BUTTON_SELECTOR);
  if (!(button instanceof HTMLElement)) return false;
  button.click();
  return true;
}

export function useTableKeyboardNavigation<T>({
  enabled,
  keysActive,
  rows,
  onClickRow,
  containerRef
}: {
  enabled: boolean;
  keysActive: boolean;
  rows: Row<T>[];
  onClickRow?: (row: Row<T>, event?: MouseEvent) => void;
  containerRef: RefObject<HTMLElement | null>;
}): { activeRowId: string | null } {
  const [activeRowId, setActiveRowId] = useState<string | null>(null);
  const activeRowIdRef = useRef(activeRowId);
  const rowsRef = useRef(rows);
  const onClickRowRef = useRef(onClickRow);
  const containerRefLatest = useRef(containerRef);
  const keysActiveRef = useRef(keysActive);
  const persistSelectFirstRef = useRef(false);
  const persistSelectFirstFromKeyRef = useRef<string | null>(null);
  const persistSelectFirstTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const rowIdsKey = rows.map((row) => row.id).join("\0");

  activeRowIdRef.current = activeRowId;
  rowsRef.current = rows;
  onClickRowRef.current = onClickRow;
  containerRefLatest.current = containerRef;
  keysActiveRef.current = keysActive;

  useEffect(() => {
    if (!keysActive) return;

    if (persistSelectFirstRef.current) {
      const firstId = rows[0]?.id ?? null;
      const selectFirstRow = () => {
        if (activeRowIdRef.current === firstId) return;
        activeRowIdRef.current = firstId;
        setActiveRowId(firstId);
        if (firstId) {
          findRowElement(containerRefLatest.current.current, firstId)?.scrollIntoView({
            block: "nearest"
          });
        }
      };

      if (rowIdsKey === persistSelectFirstFromKeyRef.current) {
        selectFirstRow();
        return;
      }

      // Apollo often clears `data` on variable change while `loading` stays
      // false (Enter before search debounce). Do not consume persist on that
      // empty gap — wait for the first non-empty replacement or the 5s timer.
      if (rows.length === 0) {
        if (activeRowId) {
          activeRowIdRef.current = null;
          setActiveRowId(null);
        }
        return;
      }

      persistSelectFirstRef.current = false;
      persistSelectFirstFromKeyRef.current = null;
      if (persistSelectFirstTimerRef.current != null) {
        clearTimeout(persistSelectFirstTimerRef.current);
        persistSelectFirstTimerRef.current = null;
      }
      selectFirstRow();
      return;
    }

    if (!activeRowId) return;
    if (rows.some((row) => row.id === activeRowId)) return;
    activeRowIdRef.current = null;
    setActiveRowId(null);
  }, [activeRowId, keysActive, rowIdsKey, rows]);

  useEffect(() => {
    const clearPersistSelectFirstTimer = () => {
      if (persistSelectFirstTimerRef.current != null) {
        clearTimeout(persistSelectFirstTimerRef.current);
        persistSelectFirstTimerRef.current = null;
      }
    };

    const clearPersistSelectFirst = () => {
      persistSelectFirstRef.current = false;
      persistSelectFirstFromKeyRef.current = null;
      clearPersistSelectFirstTimer();
    };

    if (!enabled) {
      activeRowIdRef.current = null;
      setActiveRowId(null);
      clearPersistSelectFirst();
      return;
    }

    const move = (delta: 1 | -1) => {
      clearPersistSelectFirst();
      const ids = rowsRef.current.map((row) => row.id);
      if (ids.length === 0) return;

      // First j or k (no selection yet) always lands on the first row.
      const currentIndex = activeRowIdRef.current ? ids.indexOf(activeRowIdRef.current) : -1;
      const nextIndex = currentIndex === -1 ? 0 : Math.max(0, Math.min(ids.length - 1, currentIndex + delta));
      const nextId = ids[nextIndex];
      if (!nextId) return;

      activeRowIdRef.current = nextId;
      setActiveRowId(nextId);
      findRowElement(containerRefLatest.current.current, nextId)?.scrollIntoView({
        block: "nearest"
      });
    };

    const findActiveRow = () => {
      const id = activeRowIdRef.current;
      if (!id) return null;
      return rowsRef.current.find((row) => row.id === id) ?? null;
    };

    const open = () => {
      // Search Enter highlights the current first row. Open that highlighted
      // row even while persist is armed (People results often already match).
      // Persist still re-selects row 1 on the first later id change unless
      // this open succeeds.
      const row = findActiveRow();
      if (!row) return false;
      const rowElement = findRowElement(containerRefLatest.current.current, row.id);
      if (clickRowLink(rowElement)) {
        clearPersistSelectFirst();
        return true;
      }
      if (!onClickRowRef.current) return false;
      clearPersistSelectFirst();
      onClickRowRef.current(row);
      return true;
    };

    const preview = () => {
      const row = findActiveRow();
      if (!row) return false;
      const rowElement = findRowElement(containerRefLatest.current.current, row.id);
      if (!clickPreviewButton(rowElement)) return false;
      clearPersistSelectFirst();
      return true;
    };

    const selectFirst = () => {
      persistSelectFirstRef.current = true;
      persistSelectFirstFromKeyRef.current = rowsRef.current.map((row) => row.id).join("\0");
      clearPersistSelectFirstTimer();
      persistSelectFirstTimerRef.current = setTimeout(() => {
        persistSelectFirstRef.current = false;
        persistSelectFirstFromKeyRef.current = null;
        persistSelectFirstTimerRef.current = null;
      }, PERSIST_SELECT_FIRST_MS);
      const firstId = rowsRef.current[0]?.id;
      if (!firstId) return;

      activeRowIdRef.current = firstId;
      setActiveRowId(firstId);
      findRowElement(containerRefLatest.current.current, firstId)?.scrollIntoView({
        block: "nearest"
      });
    };

    const unregister = registerPageTableKeyboard({ move, selectFirst, open, preview });

    const handleKeyDown = (event: KeyboardEvent) => {
      const controller = getSolePageTableKeyboard();
      if (!controller) return;

      if (event.metaKey || event.ctrlKey || event.altKey) return;
      if (hasOpenOverlay() || hasTransientPopupOpen()) return;
      if (document.body.dataset.goNavMode === "true") return;

      // Enter/Escape in page search select the first row without opening,
      // including while the table is loading (keysActive false). SearchInput
      // still blurs (Enter) or clears then blurs (Escape). A following
      // Enter/o/Space opens or previews the highlighted row. Persist stays
      // armed until the first non-empty row-id change (an empty list during
      // debounce / Apollo variable change does not count; People/Tasks refetch
      // may keep loading false) or a 5s fallback so a later sort does not snap
      // to row 1. Click-away blur does not select. j/k/o/Space stay skipped
      // while loading.
      if (isPageSearchFromKeyEvent(event) && (event.key === "Enter" || event.key === "Escape")) {
        if (event.repeat) return;
        controller.selectFirst();
        return;
      }

      if (!keysActiveRef.current) return;

      const key = event.key.length === 1 ? event.key.toLowerCase() : event.key;
      const isMoveKey = key === "j" || key === "k";
      const isOpenKey = key === "o" || event.key === "Enter";
      const isPreviewKey = event.key === " " || event.key === "Spacebar";

      if (!isMoveKey && !isOpenKey && !isPreviewKey) return;
      if (shouldIgnoreTableShortcut(event, { allowRepeat: isMoveKey })) return;

      // Enter/Space stay with focused native and Material hosts. j/k/o still
      // move and open after Space leaves focus on the row view button.
      if ((event.key === "Enter" || isPreviewKey) && isInteractiveFromKeyEvent(event)) return;

      if (isMoveKey) {
        event.preventDefault();
        controller.move(key === "j" ? 1 : -1);
        return;
      }

      if (isOpenKey) {
        if (controller.open()) event.preventDefault();
        return;
      }

      // Always swallow Space so it never scrolls the page. Preview only when
      // the row has expand-details; otherwise Space is a no-op.
      event.preventDefault();
      controller.preview();
    };

    document.addEventListener("keydown", handleKeyDown, true);
    return () => {
      unregister();
      document.removeEventListener("keydown", handleKeyDown, true);
      clearPersistSelectFirstTimer();
    };
  }, [enabled]);

  return { activeRowId: enabled && keysActive ? activeRowId : null };
}
